import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  acceptAllConsent,
  hasConsentDecision,
  readConsent,
  rejectNonEssentialConsent,
  saveConsent,
  setDoNotSellOrShare,
} from "../util/cookieConsent";

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => readConsent());
  const [bannerOpen, setBannerOpen] = useState(() => !hasConsentDecision());
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const onChange = (e) => setConsent(e.detail || readConsent());
    window.addEventListener("wv:cookie-consent", onChange);
    return () => window.removeEventListener("wv:cookie-consent", onChange);
  }, []);

  const acceptAll = useCallback(() => {
    const next = acceptAllConsent();
    setConsent(next);
    setBannerOpen(false);
    setPreferencesOpen(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    const next = rejectNonEssentialConsent();
    setConsent(next);
    setBannerOpen(false);
    setPreferencesOpen(false);
  }, []);

  const savePreferences = useCallback((prefs) => {
    const next = saveConsent(prefs);
    setConsent(next);
    setBannerOpen(false);
    setPreferencesOpen(false);
  }, []);

  const optOutOfSaleOrShare = useCallback(() => {
    const next = setDoNotSellOrShare(true);
    setConsent(next);
  }, []);

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true);
    setBannerOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      consent,
      bannerOpen,
      preferencesOpen,
      setPreferencesOpen,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      optOutOfSaleOrShare,
      openPreferences,
      allowsFunctional: consent?.functional ?? false,
      allowsAnalytics: consent?.analytics ?? false,
      doNotSellOrShare: consent?.doNotSellOrShare ?? false,
    }),
    [
      consent,
      bannerOpen,
      preferencesOpen,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      optOutOfSaleOrShare,
      openPreferences,
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return ctx;
}

/** Safe hook for shared components that may render outside the provider. */
export function useCookieConsentOptional() {
  return useContext(CookieConsentContext);
}
